import { useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Button } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getRack, getItems } from '../../services'
import { Item } from '../../types/response'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Server, X } from 'lucide-react'
import RackSlot from './RackSlot'
import { paths } from '@/utils/path'
import {
    getStatusBadgeVariant,
    getConditionBadgeVariant,
    formatDateTime,
} from '../../utils'

/**
 * Parse form factor string to get U height
 * Examples: "1U", "2U", "4U" -> 1, 2, 4
 */
const parseFormFactor = (formFactor?: string): number => {
    if (!formFactor) return 1
    const match = formFactor.match(/(\d+)U/i)
    return match ? parseInt(match[1], 10) : 1
}

/**
 * Status legend component - shows color meanings for rack slots
 * Colors match RackSlot getStatusColor (80% opacity)
 */
const StatusLegend = () => (
    <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
        <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500/80 rounded-sm" />
            <span>In Use</span>
        </div>
        <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-blue-500/80 rounded-sm" />
            <span>Available</span>
        </div>
        <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-yellow-500/80 rounded-sm" />
            <span>Reserved</span>
        </div>
        <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-orange-500/80 rounded-sm" />
            <span>Maintenance</span>
        </div>
        <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-red-500/80 rounded-sm" />
            <span>Faulty</span>
        </div>
        <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-gray-500/80 rounded-sm" />
            <span>Other</span>
        </div>
    </div>
)

/**
 * Equipment detail panel - shown on the right when clicking an equipment slot
 */
const EquipmentPanel = ({
    equipment,
    onClose,
}: {
    equipment: Item
    onClose: () => void
}) => {
    const navigate = useNavigate()
    // Fetch linked components
    const { data: componentsData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.WAREHOUSE_ITEMS,
                'rack-panel-components',
                equipment.id,
            ],
            queryFn: () =>
                getItems({
                    parent_id: equipment.id,
                    item_type: 'component',
                    page_size: 100,
                }),
            staleTime: 5 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    const components = componentsData?.results || []

    // Group components by category
    const groupedComponents = useMemo(() => {
        const groups: Record<string, Item[]> = {}
        components.forEach((c: Item) => {
            const cat = c.category || 'other'
            if (!groups[cat]) groups[cat] = []
            groups[cat].push(c)
        })
        return groups
    }, [components])

    return (
        <Card className="lg:col-span-4 sticky top-16 self-start max-h-[calc(100vh-5rem)] overflow-y-auto">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">Equipment Detail</CardTitle>
                    <button
                        onClick={onClose}
                        className="p-1 rounded hover:bg-muted transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Basic Info */}
                <div className="space-y-2">
                    <div>
                        <p className="text-sm font-semibold">
                            {equipment.name}
                        </p>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() =>
                                navigate(
                                    `${paths.warehouseEquipment}/${equipment.id}`
                                )
                            }
                        >
                            View full detail
                        </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {equipment.service_tag && (
                            <>
                                <span className="text-muted-foreground">
                                    Service Tag
                                </span>
                                <span className="font-medium">
                                    {equipment.service_tag}
                                </span>
                            </>
                        )}
                        {equipment.serial_number && (
                            <>
                                <span className="text-muted-foreground">
                                    Serial Number
                                </span>
                                <span className="font-medium">
                                    {equipment.serial_number}
                                </span>
                            </>
                        )}
                        <span className="text-muted-foreground">Category</span>
                        <span className="capitalize">{equipment.category}</span>
                        <span className="text-muted-foreground">Status</span>
                        <Badge
                            variant={getStatusBadgeVariant(equipment.status)}
                            className="w-fit"
                        >
                            {equipment.status?.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <span className="text-muted-foreground">Condition</span>
                        <Badge
                            variant={getConditionBadgeVariant(
                                equipment.condition
                            )}
                            className="w-fit"
                        >
                            {equipment.condition?.toUpperCase()}
                        </Badge>
                        {equipment.rack_position_u && (
                            <>
                                <span className="text-muted-foreground">
                                    Position
                                </span>
                                <span className="font-medium text-blue-600">
                                    U{equipment.rack_position_u}
                                </span>
                            </>
                        )}
                        {equipment.form_factor && (
                            <>
                                <span className="text-muted-foreground">
                                    Form Factor
                                </span>
                                <span>{equipment.form_factor}</span>
                            </>
                        )}
                        {equipment.model?.name && (
                            <>
                                <span className="text-muted-foreground">
                                    Model
                                </span>
                                <span>{equipment.model.name}</span>
                            </>
                        )}
                        {equipment.supplier?.name && (
                            <>
                                <span className="text-muted-foreground">
                                    Supplier
                                </span>
                                <span>{equipment.supplier.name}</span>
                            </>
                        )}
                        {equipment.owner && (
                            <>
                                <span className="text-muted-foreground">
                                    Owner
                                </span>
                                <span>{equipment.owner}</span>
                            </>
                        )}
                        {equipment.current_location?.name && (
                            <>
                                <span className="text-muted-foreground">
                                    Location
                                </span>
                                <span>{equipment.current_location.name}</span>
                            </>
                        )}
                        {equipment.purchase_date && (
                            <>
                                <span className="text-muted-foreground">
                                    Purchase Date
                                </span>
                                <span>
                                    {formatDateTime(equipment.purchase_date)}
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* Linked Components */}
                <div className="border-t pt-3">
                    <p className="text-xs font-semibold mb-2">
                        Linked Components
                        {components.length > 0 && (
                            <Badge variant="outline" className="ml-2">
                                {components.length}
                            </Badge>
                        )}
                    </p>

                    {components.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            No components installed
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {Object.entries(groupedComponents).map(
                                ([category, items]) => (
                                    <div key={category}>
                                        <p className="text-[10px] font-medium text-muted-foreground uppercase mb-1">
                                            {category} ({items.length})
                                        </p>
                                        <div className="space-y-1">
                                            {items.map((comp) => (
                                                <Link
                                                    key={comp.id}
                                                    to={`${paths.warehouseComponents}/${comp.id}`}
                                                    className="block text-xs p-1.5 rounded bg-muted/50 hover:bg-muted transition-colors"
                                                >
                                                    <span className="font-medium text-blue-600 hover:underline">
                                                        {comp.name}
                                                    </span>
                                                    {comp.ref && (
                                                        <span className="text-muted-foreground ml-1">
                                                            ({comp.ref})
                                                        </span>
                                                    )}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}

/**
 * RackLayoutView - Visual representation of rack U positions with equipment
 * Shows front and rear views side by side (NetBox style)
 * Equipment is positioned based on rack_position_u and rack_face fields
 */
const RackLayoutView = () => {
    const { id } = useParams<{ id: string }>()
    const [selectedEquipment, setSelectedEquipment] = useState<Item | null>(
        null
    )

    // Fetch rack data
    const { data: rack, renderState: rackRenderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_RACKS, id],
        queryFn: () => getRack(id!),
    })

    // Fetch equipment in this rack
    const { data: itemsResponse, renderState: itemsRenderState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.WAREHOUSE_ITEMS, 'rack-layout', id],
            queryFn: () =>
                getItems({
                    rack_id: id,
                    item_type: 'equipment',
                    page_size: 100,
                }),
        })

    // Build position maps for front/rear and separate unpositioned equipment
    const {
        frontPositionMap,
        rearPositionMap,
        frontOccupied,
        rearOccupied,
        unpositionedEquipment,
    } = useMemo(() => {
        const frontMap = new Map<number, { equipment: Item; height: number }>()
        const rearMap = new Map<number, { equipment: Item; height: number }>()
        const frontOcc = new Set<number>()
        const rearOcc = new Set<number>()
        const unpositioned: Item[] = []
        const items = itemsResponse?.results || []

        items.forEach((item) => {
            if (item.rack_position_u) {
                const height = parseFormFactor(item.form_factor)
                // Use rack_face field if available
                const face = (item as Item & { rack_face?: string }).rack_face

                if (face === 'rear') {
                    // Explicitly rear-mounted
                    rearMap.set(item.rack_position_u, {
                        equipment: item,
                        height,
                    })
                    for (let i = 1; i < height; i++) {
                        rearOcc.add(item.rack_position_u - i)
                    }
                } else if (face === 'front') {
                    // Explicitly front-mounted
                    frontMap.set(item.rack_position_u, {
                        equipment: item,
                        height,
                    })
                    for (let i = 1; i < height; i++) {
                        frontOcc.add(item.rack_position_u - i)
                    }
                } else {
                    // No face specified - show on both views
                    frontMap.set(item.rack_position_u, {
                        equipment: item,
                        height,
                    })
                    rearMap.set(item.rack_position_u, {
                        equipment: item,
                        height,
                    })
                    for (let i = 1; i < height; i++) {
                        frontOcc.add(item.rack_position_u - i)
                        rearOcc.add(item.rack_position_u - i)
                    }
                }
            } else {
                unpositioned.push(item)
            }
        })

        return {
            frontPositionMap: frontMap,
            rearPositionMap: rearMap,
            frontOccupied: frontOcc,
            rearOccupied: rearOcc,
            unpositionedEquipment: unpositioned,
        }
    }, [itemsResponse])

    // Handle slot click - show equipment detail panel
    const handleSlotClick = (_position: number, equipment?: Item) => {
        if (equipment) {
            setSelectedEquipment(equipment)
        }
    }

    // Render loading/error states
    if (rackRenderState()) return rackRenderState()
    if (itemsRenderState()) return itemsRenderState()

    const uHeight = rack?.u_height || 42
    const totalEquipment = itemsResponse?.results?.length || 0
    const unpositionedCount = unpositionedEquipment.length
    // Calculate used slots from original items (not maps) to avoid double-counting
    const usedSlots = (itemsResponse?.results || [])
        .filter((item) => item.rack_position_u)
        .reduce((sum, item) => sum + parseFormFactor(item.form_factor), 0)
    const positionedCount = totalEquipment - unpositionedCount
    const freeSlots = uHeight - usedSlots

    // Generate slots for a face (front or rear)
    const generateSlots = (
        positionMap: Map<number, { equipment: Item; height: number }>,
        occupiedPositions: Set<number>
    ) => {
        const slots = []
        for (let u = uHeight; u >= 1; u--) {
            if (occupiedPositions.has(u)) continue

            const posData = positionMap.get(u)
            if (posData) {
                slots.push(
                    <RackSlot
                        key={u}
                        position={u}
                        equipment={posData.equipment}
                        isTopOfEquipment={true}
                        equipmentHeight={posData.height}
                        onClick={handleSlotClick}
                        isSelected={
                            selectedEquipment?.id === posData.equipment.id
                        }
                    />
                )
            } else {
                slots.push(
                    <RackSlot key={u} position={u} onClick={handleSlotClick} />
                )
            }
        }
        return slots
    }

    const frontSlots = generateSlots(frontPositionMap, frontOccupied)
    const rearSlots = generateSlots(rearPositionMap, rearOccupied)

    return (
        <div className="mt-2 grid grid-cols-1 lg:grid-cols-12 gap-2">
            {/* Front View */}
            <Card
                className={
                    selectedEquipment ? 'lg:col-span-3' : 'lg:col-span-4'
                }
            >
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-center">Front</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                    <div className="border rounded-md overflow-hidden">
                        {frontSlots}
                    </div>
                    <StatusLegend />
                </CardContent>
            </Card>

            {/* Rear View */}
            <Card
                className={
                    selectedEquipment ? 'lg:col-span-3' : 'lg:col-span-4'
                }
            >
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-center">Rear</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                    <div className="border rounded-md overflow-hidden">
                        {rearSlots}
                    </div>
                    <StatusLegend />
                </CardContent>
            </Card>

            {/* Summary Stats */}
            <Card
                className={
                    selectedEquipment ? 'lg:col-span-2' : 'lg:col-span-4'
                }
            >
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Server className="w-4 h-4" />
                        {rack?.name || rack?.code}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                                Total Height
                            </span>
                            <span className="font-medium">{uHeight}U</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                                Total Equipment
                            </span>
                            <span className="font-medium">
                                {totalEquipment}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                                Positioned
                            </span>
                            <Badge variant="secondary">{positionedCount}</Badge>
                        </div>
                        {unpositionedCount > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                    No Position
                                </span>
                                <Badge variant="outline">
                                    {unpositionedCount}
                                </Badge>
                            </div>
                        )}
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Used</span>
                            <Badge variant="secondary">{usedSlots}U</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Free</span>
                            <Badge
                                variant={
                                    freeSlots > 0 ? 'default' : 'destructive'
                                }
                            >
                                {freeSlots}U
                            </Badge>
                        </div>
                    </div>

                    {/* Usage Bar */}
                    <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Utilization</span>
                            <span>
                                {((usedSlots / uHeight) * 100).toFixed(0)}%
                            </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                                className="h-full bg-primary transition-all"
                                style={{
                                    width: `${(usedSlots / uHeight) * 100}%`,
                                }}
                            />
                        </div>
                    </div>

                    {/* Unpositioned Equipment */}
                    {unpositionedEquipment.length > 0 && (
                        <div className="pt-4 border-t space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">
                                Equipment Without Position
                            </p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                                {unpositionedEquipment.map((item) => (
                                    <div
                                        key={item.id}
                                        className="text-xs p-2 bg-muted/50 rounded cursor-pointer hover:bg-muted transition-colors"
                                        onClick={() => handleSlotClick(0, item)}
                                    >
                                        <p className="font-medium truncate">
                                            {item.name}
                                        </p>
                                        <p className="text-muted-foreground">
                                            {item.category} • {item.status}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Equipment Detail Panel */}
            {selectedEquipment && (
                <EquipmentPanel
                    equipment={selectedEquipment}
                    onClose={() => setSelectedEquipment(null)}
                />
            )}
        </div>
    )
}

export default RackLayoutView
